import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requires2FA, setRequires2FA] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Verify token is still valid
      api.get('/auth/me')
        .then(res => setUser(res.data))
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    
    // Check if 2FA is required
    if (res.data.requires_2fa) {
      setRequires2FA(true);
      setPendingCredentials({ email, password });
      return { requires_2fa: true, user: res.data.user };
    }
    
    // No 2FA, complete login
    localStorage.setItem('token', res.data.access_token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    setRequires2FA(false);
    setPendingCredentials(null);
    return res.data;
  };

  const verify2FA = async (totpCode) => {
    if (!pendingCredentials) {
      throw new Error('Aucune authentification en attente');
    }
    
    const res = await api.post('/auth/login/2fa', {
      email: pendingCredentials.email,
      password: pendingCredentials.password,
      totp_code: totpCode
    });
    
    localStorage.setItem('token', res.data.access_token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    setRequires2FA(false);
    setPendingCredentials(null);
    return res.data;
  };

  const cancel2FA = () => {
    setRequires2FA(false);
    setPendingCredentials(null);
  };

  const register = async (userData) => {
    const res = await api.post('/auth/register', userData);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setRequires2FA(false);
    setPendingCredentials(null);
  };

  const refreshUser = async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      localStorage.setItem('user', JSON.stringify(res.data));
      return res.data;
    } catch (error) {
      // Token may be expired - user will be redirected to login on next protected request
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      register, 
      logout, 
      loading, 
      requires2FA, 
      verify2FA, 
      cancel2FA,
      pendingCredentials,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// API Services
export const roomsApi = {
  list: () => api.get('/rooms'),
  get: (id) => api.get(`/rooms/${id}`),
  create: (data) => api.post('/rooms', data),
  update: (id, data) => api.put(`/rooms/${id}`, data),
  delete: (id) => api.delete(`/rooms/${id}`),
};

// Password Reset API
export const passwordResetApi = {
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (email, code, new_password) => api.post('/auth/reset-password', { email, code, new_password }),
  verifyCode: (email, code) => api.post(`/auth/verify-reset-code?email=${encodeURIComponent(email)}&code=${code}`),
};

export const clientsApi = {
  list: () => api.get('/clients'),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  addContact: (id, data) => api.post(`/clients/${id}/contacts`, data),
  updateContact: (id, contactId, data) => api.put(`/clients/${id}/contacts/${contactId}`, data),
  deleteContact: (id, contactId) => api.delete(`/clients/${id}/contacts/${contactId}`),
};

export const reservationsApi = {
  list: (params) => api.get('/reservations', { params }),
  get: (id) => api.get(`/reservations/${id}`),
  create: (data) => api.post('/reservations', data),
  update: (id, data) => api.put(`/reservations/${id}`, data),
  delete: (id) => api.delete(`/reservations/${id}`),
};

export const quotesApi = {
  list: () => api.get('/quotes'),
  create: (data) => api.post('/quotes', data),
  updateStatus: (id, status) => api.put(`/quotes/${id}/status?status=${status}`),
};

export const invoicesApi = {
  list: () => api.get('/invoices'),
  create: (data) => api.post('/invoices', data),
  updateStatus: (id, status) => api.put(`/invoices/${id}/status?status=${status}`),
};

export const paymentsApi = {
  list: (invoiceId) => api.get('/payments', { params: { invoice_id: invoiceId } }),
  create: (data) => api.post('/payments', data),
};

export const tasksApi = {
  list: (params) => api.get('/tasks', { params }),
  create: (data) => api.post('/tasks', data),
  complete: (id) => api.put(`/tasks/${id}/complete`),
};

export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getUpcomingReservations: (limit = 5) => api.get(`/dashboard/upcoming-reservations?limit=${limit}`),
};

export const usersApi = {
  list: () => api.get('/users'),
  updateRole: (id, role) => api.put(`/users/${id}/role?role=${role}`),
  force2FA: (id, forceEnabled) => api.put(`/users/${id}/2fa/force`, { force_enabled: forceEnabled }),
  reset2FA: (id) => api.delete(`/users/${id}/2fa`),
};

// 2FA API
export const twoFactorApi = {
  setup: () => api.post('/auth/2fa/setup'),
  verifySetup: (code) => api.post('/auth/2fa/verify-setup', { code }),
  disable: (code) => api.post('/auth/2fa/disable', { code }),
  status: () => api.get('/auth/2fa/status'),
};

// Login Logs API
export const loginLogsApi = {
  list: (params) => api.get('/logs/logins', { params }),
  exportPdf: (params) => api.get('/logs/logins/export/pdf', { params, responseType: 'blob' }),
  delete: (id) => api.delete(`/logs/logins/${id}`),
  deleteAll: (beforeDate) => api.delete('/logs/logins', { params: { before_date: beforeDate } }),
  stats: (days = 30) => api.get('/logs/logins/stats', { params: { days } }),
};

export default api;
